import { render } from 'engine/index';
import { rpc } from 'src/backend';
import { Table, Td, Th, TrBody, TrHead } from './TableComponents';
import { Button } from './Button';
import type { KwaliteitenJachtState } from 'shared/modelTypes/kwaliteiten-jacht';

export function PlayKwaliteitenJacht({ licenseCode }: { licenseCode: string }) {
	const view: HTMLElement = <div>Will play game {licenseCode}</div>;

	const sessionId = licenseCode;

	rpc.onAdminUpdates.subscribe(
		{ sessionId },
		{
			onData(data) {
				//
				if (data.kind === 'update-state') {
					const state = data.state as KwaliteitenJachtState;
					renderStage(state);
				}
			},
		},
	);

	const renderStage = (state: KwaliteitenJachtState) => {
		const s = state.stage;
		switch (s) {
			case 'starting':
				render(view, <div class="animate-pulse text-xl text-gray-700">Starting play session</div>);
				break;
			case 'register-players':
				render(
					view,
					<PlayerRegisterView
						onContinue={(maxRounds) => {
							console.log('start the game');
							rpc.sendKwaliteitenJachtMessage.query({ sessionId, update: { kind: 'start-game', maxRounds } });
						}}
						state={state}
					/>,
				);
				break;
			case 'play-rounds':
				{
					const dagen = ['Dag 1', 'Dag 2&3', 'Dag 4', 'Dag 5&6', 'Dag 7'] as const;
					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">{dagen[state.roundIndex]}</h2>
							{state.showPreface && (
								<div>
									<p>Geef spelers uitleg en druk vervolgens op de knop om de ronde te starten.</p>
									<button
										class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
										on:click={() =>
											rpc.sendKwaliteitenJachtMessage.query({ sessionId, update: { kind: 'end-preface' } })
										}
									>
										Start round
									</button>
								</div>
							)}
							{!state.showPreface && (
								<div>
									<h3 class="font-bold">Ronde {state.subRoundIndex + 1}</h3>
									<p>Wacht tot de rondes voorbij zijn.</p>
									<button
										class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
										on:click={() => rpc.sendKwaliteitenJachtMessage.query({ sessionId, update: { kind: 'end-early' } })}
									>
										Ronde direct eindigen
									</button>
									<button
										class="ml-3 mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
										on:click={async () => {
											await rpc.sendKwaliteitenJachtMessage.query({ sessionId, update: { kind: 'end-early-day' } });
											await rpc.sendKwaliteitenJachtMessage.query({ sessionId, update: { kind: 'end-early' } });
										}}
									>
										Dag direct eindigen
									</button>
								</div>
							)}
							<PlayerTable state={state}></PlayerTable>
						</div>,
					);
				}
				break;
			case 'play-ending-game':
				render(
					view,
					<div>
						<h2 class="text-lg font-bold text-gray-900">Laatste ronde</h2>
						{state.showPreface && (
							<div>
								<button
									class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
									on:click={() => rpc.sendKwaliteitenJachtMessage.query({ sessionId, update: { kind: 'end-early' } })}
								>
									Ronde eerder stoppen
								</button>
							</div>
						)}

						{!state.showPreface && (
							<div>
								<p>Wacht tot de ronde voorbij is.</p>
								<button
									class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
									on:click={() => rpc.sendKwaliteitenJachtMessage.query({ sessionId, update: { kind: 'end-early' } })}
								>
									Eindresultaat tonen
								</button>
							</div>
						)}
						<PlayerTable state={state}></PlayerTable>
					</div>,
				);
				break;
			case 'ending-report':
				render(
					view,
					<div>
						<h2 class="text-lg font-bold text-gray-900">Showing ending report</h2>
						<PlayerTable state={state}></PlayerTable>
					</div>,
				);
				break;
		}
	};

	// render initial state
	(async () => {
		const state = await rpc.getGameState.query({ sessionId });

		renderStage(state);
	})();

	return view;
}

function PlayerRegisterView({ state, onContinue }: { state: KwaliteitenJachtState; onContinue: (maxRounds: number) => void }) {
	const playersWithName = state.players.filter((p) => p.name);

	return (
		<div>
			<h3 class="text-lg font-bold text-gray-800 dark:text-gray-200">Spelers registreren</h3>
			<p class="mb-5 text-gray-700">
				Laat spelers de QR code op het bord scannen en vervolgens hun gegevens invullen.
				<br />
				Druk op <strong>doorgaan</strong> als alle spelers gereed zijn.
			</p>
			{playersWithName.length > 0 && (
				<Table>
					<thead>
						<TrHead>
							<Th>Name</Th>
							<Th>Color</Th>
							<Th>Id</Th>
						</TrHead>
					</thead>
					<tbody>
						{playersWithName.map((player) => {
							return (
								<TrBody>
									<Td>{player.name}</Td>
									<Td>{player.color}</Td>
									<Td>{player.id}</Td>
								</TrBody>
							);
						})}
					</tbody>
				</Table>
			)}
			{!playersWithName.length && <small class="text-gray-700">Er zijn nog geen spelers.</small>}
			<div class="mt-5"></div>
			<form
				class="flex flex-col"
				on:submit={(e: SubmitEvent) => {
					e.preventDefault();
					const form = e.target as HTMLFormElement;
					const data = new FormData(form);
					const maxRounds = Number((data.get('maxRounds') ?? '') as string);
					onContinue(maxRounds || 5);
				}}
			>
				<div>
					<label class="block font-bold">Maximaal aantal rondes:</label>
					<input
						name="maxRounds"
						type="number"
						min="1"
						max="20"
						value="5"
						step="1"
						placeholder="5"
						class="mb-2 w-28 rounded-md border border-gray-300 bg-gray-50 p-1"
					/>
				</div>
				<Button type="submit">Doorgaan</Button>
			</form>
		</div>
	);
}

function PlayerTable({ state }: { state: KwaliteitenJachtState }) {
	return (
		<Table>
			<thead>
				<TrHead>
					<Th>Name</Th>
					<Th>Color</Th>
					<Th>Munten</Th>
					<Th>
						Kwaliteiten (<span class="text-theme-600">toepasselijke roze gemarkeerd</span>)
					</Th>
					<Th>id</Th>
				</TrHead>
			</thead>
			<tbody>
				{state.players.map((player) => {
					return (
						<TrBody>
							<Td>{player.name}</Td>
							<Td>{player.color}</Td>
							<Td>{player.coins}</Td>
							<Td>
								<div class="flex flex-col gap-1">
									{player.cards.map((c) => {
										return (
											<span
												class={`${
													player.relevantCards?.some((relevantCard) => relevantCard === c.name)
														? 'text-theme-600'
														: ''
												}`}
											>
												{c.name}
											</span>
										);
									})}
								</div>
							</Td>
							<Td>{player.id}</Td>
						</TrBody>
					);
				})}
			</tbody>
		</Table>
	);
}
